"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Layers, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const commitments = [{
  id: "access",
  title: "Direct Consultant Access",
  description: "You speak and collaborate directly with me on every call, email, and strategy review. No account handlers, no junior delegates.",
  iconName: "UserCheck"
}, {
  id: "strategy",
  title: "Tailored Organic Roadmaps",
  description: "Every audit, technical recommendation, and keyword map is custom-built for your business niche and commercial goals.",
  iconName: "Layers"
}, {
  id: "transparency",
  title: "Honest, Transparent Reporting",
  description: "I deliver clear monthly reporting focused on real organic revenue, lead quality, and verified search visibility, without vanity metrics.",
  iconName: "BarChart3"
}, {
  id: "implementation",
  title: "Practical Implementation",
  description: "I do not just deliver slide decks. I solve technical SEO roadblocks, optimize architecture, and guide your team on exact execution.",
  iconName: "ShieldCheck"
}];
const highlights = [{
  id: "1",
  text: "Senior freelance expertise without agency overhead"
}, {
  id: "2",
  text: "Direct communication with rapid turnaround"
}, {
  id: "3",
  text: "Zero generic templates or automated cookie-cutter audits"
}, {
  id: "4",
  text: "Practical advice geared toward measurable commercial ROI"
}];
export default function DirectPartnership() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="directpartnershiptrust" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Top Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 font-medium text-foreground bg-secondary">
            <Sparkles className="size-3.5 text-primary" />
            <span>Direct Freelance Partnership</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            You Work Directly With Me — Never Passed to an Account Manager
          </h2>

          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            When you hire YE Consultancy, you get my undivided strategic expertise, hands-on
            technical execution, and genuine accountability from start to finish.
          </p>
        </div>

        {/* 4 Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {commitments.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40 flex flex-col justify-between">
                <CardHeader className="p-6">
                  <div className="mb-4 inline-flex size-12 items-center justify-center rounded-lg bg-secondary text-primary">
                    {item.iconName === "UserCheck" && <UserCheck className="size-6 text-primary" />}
                    {item.iconName === "Layers" && <Layers className="size-6 text-primary" />}
                    {item.iconName === "BarChart3" && <BarChart3 className="size-6 text-primary" />}
                    {item.iconName === "ShieldCheck" && <ShieldCheck className="size-6 text-primary" />}
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6 pt-0">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Focus Statement & Proof Box */}
        <div className="rounded-2xl border border-border bg-muted/40 p-8 md:p-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs font-semibold tracking-wider uppercase text-primary">
                My Personal Guarantee
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground font-default">
                Straightforward Advice, Clean Execution, Real Search Growth
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                SEO is not magic, nor should it be wrapped in jargon. I diagnose your actual
                growth bottlenecks, fix critical technical flaws, optimize content architecture,
                and build sustainable authority that Google and modern AI search engines reward.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {highlights.map((point, i) => <div key={point.id} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 shrink-0 text-primary mt-1" />
                    <span className="text-xs md:text-sm text-foreground font-medium">
                      {point.text}
                    </span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col justify-center items-start lg:items-end border-t lg:border-t-0 lg:border-l border-border pt-6 lg:pt-0 lg:pl-10">
              <div className="w-full space-y-4">
                <div className="rounded-xl border border-border bg-card p-5 shadow-xs">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                    Verified Work
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    Explore real case studies structured with challenge, strategy, and verified results.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full">
                  <Button asChild size="lg" className="w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                    <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                      <span>Explore My Case Studies</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full justify-center border-border hover:bg-secondary/60 text-foreground font-medium">
                    <Link to="/ContactPage" className="inline-flex items-center gap-2">
                      <span>Book a Free SEO Consultation</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Separator className="mt-20 md:mt-28" />
    </section>;
}